import { fetchEventSource } from '@microsoft/fetch-event-source';

import { LLMModel, LLMOutPutCharType } from '@/component/fb-ai/types';
import STORE from '@/store';
import { onTokenExpired } from '@/util/authority';

import iAxios, { StatusCode } from '../IAxios';
import api from './api';
import { EventSourceReq, UseStreamCacheRsp } from './types';

export const sharedData = {
  tpilotRecordId: '',
  tpilotUserLikeRecord: '',
};

export const extractHeadersAndSetData = (response) => {
  if (response.status === StatusCode.authExpired) {
    onTokenExpired();
    return;
  }
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  const tpilotRecordId = response.headers.get('tpilot-record-id');
  if (tpilotRecordId === null) {
    throw new Error('tpilot-record-id不在the response headers中');
  }
  sharedData.tpilotRecordId = tpilotRecordId;

  const tpilotUserLikeRecord = response.headers.get('tpilot-user-like-record');
  if (tpilotUserLikeRecord === null) {
    throw new Error('tpilot-user-like-record不在response headers中');
  }
  sharedData.tpilotUserLikeRecord = tpilotUserLikeRecord;
};

export const addCommonHeader = (body: any) => {
  body.headers = {
    'Tone-Gateway-SecretId': 'tpmCaseGen',
    'Tone-Gateway-SecretKey':
      '2UNF2JhdqoArePmnL1YemJAmbxx2UNF2HtdVX7PeYJNBifDpINvtW8',
    'Content-Type': 'application/json',
    'EPP-Gateway-Env': 'test',
  };
  if (STORE?.userInfo?.token) {
    body.headers['X-Dcl-Token'] = STORE?.userInfo?.token;
  }
  body.mode = 'cors';
  body.openWhenHidden = true;
  body.credentials = 'include';
  body.user_id = STORE.user;
  return body;
};

export const getTroubleShooting = async (
  user_id: string,
  body: string,
  onMessage: (data: any) => void,
  onClose: () => void,
  onError?: (err: any) => void,
  signal?: AbortSignal,
  modelName?: LLMModel
) => {
  const req: EventSourceReq = {
    user_id,
    app_id: STORE.appId,
    task_params: {
      task_type: 'trouble-shooting',
      params: body,
    },
    use_deepseek: modelName === LLMModel.deepseek,
  };
  fetchEventSource(api.gradioStream, {
    method: 'POST',
    headers: addCommonHeader({}).headers,
    body: JSON.stringify(req),
    signal,
    openWhenHidden: true,
    onopen: async (response) => {
      try {
        await extractHeadersAndSetData(response);
      } catch (error) {
        console.error('提取响应头时出错:', error);
        throw error;
      }
    },
    onclose() {
      onClose();
    },
    onmessage(event) {
      const message = JSON.parse(event.data);
      const data = message?.data;
      let type = message?.type;
      // 如果type不是枚举中的类型，则默认设置为content,避免出现异常
      if (
        !Object.values(LLMOutPutCharType).includes(type as LLMOutPutCharType)
      ) {
        type = LLMOutPutCharType.content;
      }
      if (data) {
        onMessage({
          data,
          cache: false,
          type,
        });
      }
      if (type === 'close') {
        onClose();
      }
    },
    onerror(err) {
      onError?.(err);
      throw err;
    },
  });
};

export const getProbSummarize = async (
  re_gen: boolean,
  user_id: string,
  body: string,
  requestId: string,
  onMessage: (data: any) => void,
  onClose: () => void,
  onError?: (err: any) => void,
  signal?: AbortSignal,
  modelName?: LLMModel
) => {
  const req: EventSourceReq = {
    re_gen,
    user_id,
    request_id: requestId,
    app_id: STORE.appId,
    task_params: {
      task_type: 'problem_summarize',
      params: body,
    },
    use_deepseek: modelName === LLMModel.deepseek,
  };

  if (!re_gen) {
    const res = await useStreamCache(req);
    const { use_cache: useCache } = res;
    if (useCache) {
      const { data, record_id: recordId, user_like: userLike, refer } = res;
      sharedData.tpilotRecordId = recordId;
      sharedData.tpilotUserLikeRecord = String(userLike);
      onMessage({
        data,
        cache: true,
        refer,
      });
      return data;
    }
  }

  fetchEventSource(api.gradioStream, {
    method: 'POST',
    headers: addCommonHeader({}).headers,
    body: JSON.stringify(req),
    signal,
    openWhenHidden: true,
    onopen: async (response) => {
      try {
        await extractHeadersAndSetData(response);
      } catch (error) {
        console.error('提取响应头时出错:', error);
        throw error;
      }
    },
    onclose() {
      onClose();
    },
    onmessage(event) {
      const message = JSON.parse(event.data);
      const data = message?.data;
      const type = message?.type;
      if (data) {
        onMessage({
          data,
          cache: false,
          type:
            type === LLMOutPutCharType.reasoningContent
              ? LLMOutPutCharType.reasoningContent
              : LLMOutPutCharType.content,
        });
      }
      if (type === 'close') {
        onClose();
      }
    },
    onerror(err) {
      onError?.(err);
      throw err;
    },
  });
};

export const logConvertFileTree = async (
  user_id: string,
  body: string,
  requestId: string,
  onMessage: (data: any) => void,
  onClose: () => void,
  onError?: (err: any) => void,
  signal?: AbortSignal
) => {
  const req: EventSourceReq = {
    re_gen: false,
    user_id,
    app_id: STORE.appId,
    request_id: requestId,
    task_params: {
      task_type: 'convert_file',
      params: body,
    },
  };

  fetchEventSource(api.gradioStream, {
    method: 'POST',
    headers: addCommonHeader({}).headers,
    body: JSON.stringify(req),
    signal,
    openWhenHidden: true,
    onclose() {
      onClose();
    },
    onmessage(event) {
      const message = JSON.parse(event.data);
      const data = message?.data;
      const type = message?.type;
      if (data) {
        onMessage({
          data,
          cache: false,
        });
      }
      if (type === 'close') {
        onClose();
      }
    },
    onerror(err) {
      onError?.(err);
      throw err;
    },
  });
};

export const globalSearchFile = async (
  user_id: string,
  body: string,
  requestId: string,
  onMessage: (data: any) => void,
  onClose: () => void,
  onError?: (err: any) => void,
  signal?: AbortSignal
) => {
  const req: EventSourceReq = {
    re_gen: true,
    user_id,
    app_id: STORE.appId,
    request_id: requestId,
    task_params: {
      task_type: 'file_global_search',
      params: body,
    },
  };

  fetchEventSource(api.gradioStream, {
    method: 'POST',
    headers: addCommonHeader({}).headers,
    body: JSON.stringify(req),
    signal,
    openWhenHidden: true,
    onclose() {
      onClose();
    },
    onmessage(event) {
      onMessage({
        event,
        cache: false,
      });
    },
    onerror(err) {
      onError?.(err);
      throw err;
    },
  });
};

export const chatWithLog = async (
  user_id: string,
  body: string,
  request_id: string,
  onMessage: (data: any) => void,
  onClose: () => void,
  onError?: (err: any) => void,
  signal?: AbortSignal
) => {
  const req: EventSourceReq = {
    re_gen: true,
    user_id,
    app_id: STORE.appId,
    request_id,
    task_params: {
      task_type: 'chat_with_log',
      params: body,
    },
  };

  fetchEventSource(api.gradioStream, {
    method: 'POST',
    headers: addCommonHeader({}).headers,
    body: JSON.stringify(req),
    signal,
    openWhenHidden: true,
    onclose() {
      onClose();
    },
    onmessage(event) {
      const message = JSON.parse(event.data);
      const data = message?.data;
      const type = message?.type;
      if (data) {
        onMessage({
          data,
          cache: false,
          type:
            type === LLMOutPutCharType.reasoningContent
              ? LLMOutPutCharType.reasoningContent
              : LLMOutPutCharType.content,
        });
      }
      if (type === 'close') {
        onClose();
      }
    },
    onerror(err) {
      onError?.(err);
      throw err;
    },
  });
};

/**
 * 智能体调试测试
 * @param agentId 智能体ID
 * @param agentQuery agent_query 对象，由用户自由编辑并直接透传给后端，结构不固定。
 *   常见字段示例：{ fids: string[], key: string }，但不作强约束
 * @param onMessage 消息回调
 * @param onClose 关闭回调
 * @param onError 错误回调
 * @param signal 中断信号
 */
export const testAgent = async (
  agentId: number,
  agentQuery: Record<string, any>,
  onMessage: (data: any) => void,
  onClose: () => void,
  onError?: (err: any) => void,
  signal?: AbortSignal
) => {
  const req: EventSourceReq = {
    user_id: STORE.user,
    app_id: STORE.appId,
    task_params: {
      task_type: 'agent_debug',
      params: JSON.stringify({
        agent_id: agentId,
        agent_query: agentQuery,
        use_mock_write_tools: true,
      }),
    },
    request_id: crypto.randomUUID(),
  };
  fetchEventSource(api.gradioStream, {
    method: 'POST',
    headers: addCommonHeader({}).headers,
    body: JSON.stringify(req),
    signal,
    openWhenHidden: true,
    onclose() {
      onClose();
    },
    onmessage(event) {
      const message = JSON.parse(event.data);
      const data = message?.data;
      const type = message?.type;
      if (data) {
        onMessage({
          data,
          type,
        });
      }
      if (type === 'close') {
        onClose();
      }
    },
    onerror(err) {
      onError?.(err);
      throw err;
    },
  });
};

export const useStreamCache = (data: EventSourceReq) =>
  iAxios.request<UseStreamCacheRsp>({
    url: api.useStreamCache,
    method: 'post',
    data,
  });
